import { COUNTRY_ISO2, DEFAULT_COUNTRY_ISO2 } from '../geo/geoConstants';
import {
  EXCEL_HEADERS_BY_COUNTRY,
  REQUIRED_ROW_FIELDS_BY_COUNTRY,
} from './orderUploadConstants';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Body of 7-8 digits, dot separated, plus a check digit that may be a K. */
const RUT_PATTERN = /^\d{1,3}(?:\.\d{3}){1,2}-[\dkK]$/;

/**
 * @param {string} email
 * @returns {boolean}
 */
export const isValidEmail = (email) => EMAIL_PATTERN.test(email);

/**
 * Formats a Chilean RUT as `99.999.999-9`.
 *
 * Unlike the legacy digit-only mask, the check digit may be a K, which is
 * valid and was impossible to type before.
 *
 * @param {string} value
 * @returns {string}
 */
export const formatRut = (value) => {
  const clean = String(value || '')
    .replace(/[^0-9kK]/g, '')
    .toUpperCase()
    .slice(0, 9);

  if (clean.length <= 1) return clean;

  const body = clean.slice(0, -1);
  const checkDigit = clean.slice(-1);
  const groupedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${groupedBody}-${checkDigit}`;
};

/**
 * Formats the recipient tax id for the given country: a masked RUT in Chile,
 * a plain uppercase RFC in Mexico.
 *
 * @param {string} value
 * @param {string} countryIso2
 * @returns {string}
 */
export const formatTaxId = (value, countryIso2) => {
  if (countryIso2 === COUNTRY_ISO2.MX) {
    return String(value || '')
      .replace(/[^0-9a-zA-Z]/g, '')
      .toUpperCase()
      .slice(0, 13);
  }
  return formatRut(value);
};

/**
 * The tax id with nothing but its own characters: no dots, no hyphen, upper
 * case. The inverse of `formatTaxId`, and what gets stored.
 *
 * A RUT is shown masked and kept plain, so whoever reads the column — or sends
 * it to a courier that rejects punctuation — gets the value and not its
 * presentation.
 *
 * @param {string} value
 * @returns {string}
 */
export const stripTaxId = (value) =>
  String(value || '')
    .replace(/[^0-9a-zA-Z]/g, '')
    .toUpperCase();

/**
 * Reads a spreadsheet cell as trimmed text.
 *
 * Cells are not necessarily strings: a numeric reference id or postal code
 * arrives as a number, which is why the value is coerced before trimming.
 *
 * @param {*} value
 * @returns {string}
 */
const readCell = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

/**
 * Validates the parsed rows of an orders spreadsheet before uploading it.
 *
 * Stops at the first offending row and names it, so the operator can fix the
 * file without a round trip to the server.
 *
 * @param {Array<object>} rows - Sheet parsed with a header row
 * @param {string} countryIso2
 * @returns {{isValid: boolean, message: string}}
 */
export const validateOrdersSheet = (rows, countryIso2) => {
  const country = EXCEL_HEADERS_BY_COUNTRY[countryIso2]
    ? countryIso2
    : DEFAULT_COUNTRY_ISO2;

  const expectedHeaders = EXCEL_HEADERS_BY_COUNTRY[country];
  const requiredFields = REQUIRED_ROW_FIELDS_BY_COUNTRY[country];

  if (!Array.isArray(rows) || rows.length === 0 || !rows[0]) {
    return { isValid: false, message: 'El archivo está vacío, no hay órdenes que cargar.' };
  }

  const headers = Object.keys(rows[0]);
  const missingHeaders = expectedHeaders.filter((header) => !headers.includes(header));

  if (missingHeaders.length > 0) {
    return {
      isValid: false,
      message: `Faltan columnas requeridas: ${missingHeaders.join(', ')}. Descarga la plantilla actualizada.`,
    };
  }

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    // Row 1 holds the headers, so data starts on row 2.
    const rowNumber = index + 2;

    const emptyField = requiredFields.find((field) => !readCell(row[field]));
    if (emptyField) {
      return { isValid: false, message: `Falta "${emptyField}" en la fila ${rowNumber}.` };
    }

    const packages = Number(readCell(row['Bultos']));
    if (!Number.isInteger(packages) || packages <= 0) {
      return {
        isValid: false,
        message: `"Bultos" debe ser un número entero mayor a cero en la fila ${rowNumber}.`,
      };
    }

    const email = readCell(row['Correo']);
    if (email && !isValidEmail(email)) {
      return { isValid: false, message: `El correo de la fila ${rowNumber} tiene un formato inválido.` };
    }

    if (country === COUNTRY_ISO2.CL) {
      const rut = readCell(row['Rut']);
      if (rut && !RUT_PATTERN.test(rut)) {
        return {
          isValid: false,
          message: `El RUT de la fila ${rowNumber} debe tener el formato 99.999.999-9.`,
        };
      }
    }
  }

  return { isValid: true, message: '' };
};

/**
 * Builds the shipping address in the shape the backend expects for a country.
 *
 * Chile is addressed by region and commune alone; Mexico additionally needs the
 * settlement and the postal code.
 *
 * @param {object} form - Manual form values
 * @param {string} countryIso2
 * @returns {object}
 */
export const buildShippingAddress = (form, countryIso2) => {
  const base = {
    country: countryIso2,
    region: form.region,
    commune: form.commune,
    street: form.street,
    street_number: form.streetNumber,
  };

  if (countryIso2 === COUNTRY_ISO2.MX) {
    return {
      ...base,
      settlement: form.settlement,
      postal_code: form.postalCode,
    };
  }

  return base;
};
